/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Box, IconButton, Tooltip } from "@chakra-ui/react";
import { captureException } from "@sentry/react";
import type {
  Embed,
  IEmbedConfiguration,
  Report,
  models,
} from "powerbi-client";
import { PowerBIEmbed as PowerBI } from "powerbi-client-react";
import type { EventHandler } from "powerbi-client-react";
import { useState, useRef, useCallback } from "react";
import { MdFullscreen } from "react-icons/md";
import { useParams } from "react-router-dom";

import { useAppSelector } from "@/reduxHooks";
import { AnalyticsEvents } from "@/utils/analytics-events";
import { analyticsService } from "@/utils/analytics-service";

import { selectSessionId } from "../redux/selectors";

import { ErrorModal } from "./ErrorModal";
import { LoadingView } from "./LoadingView";


interface PowerBIEmbedProps {
  embedConfig: IEmbedConfiguration;
  onEmbedded: (embeddedReport: Embed) => void;
}

const DEFAULT_EMBED_SETTINGS = {
  panes: {
    filters: {
      expanded: false,
      visible: false,
    },
  },
  // Disable tooltips to reduce filter serialization issues
  interactions: {
    tooltips: false,
  },
  // Disable bottom navigation bar
  navContentPaneEnabled: false,
  // Maintain visual rendered events
  visualRenderedEvents: true,
};

export const PowerBIEmbed = ({
  embedConfig,
  onEmbedded,
}: PowerBIEmbedProps): JSX.Element => {
  const { analysisId } = useParams();
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reportRef = useRef<Report | null>(null);
  const ignoreNextError = useRef(false);
  const sessionId = useAppSelector(selectSessionId);
  const handleClose = () => {
    setError(null);
    setIsLoaded(false);
  };

  const handleRefresh = () => {
    setError(null);
    setIsLoaded(false);
    window.location.reload();
  };

  const enableFullScreen = useCallback(() => {
    if (!reportRef.current) return;

    try {
      reportRef.current.fullscreen();
    } catch (err) {
      console.error("Error toggling fullscreen:", err);
    }
  }, []);

  const logErrorToSentry = (errorMessage: string, errorDetail: unknown) => {
    captureException(new Error(`PowerBI Error: ${errorMessage}`), {
      tags: {
        errorType: "powerbi_embed_error",
      },
      extra: {
        detail: errorDetail,
      },
    });
  };

  const handleEmbedded = (embeddedReport: Embed) => {
    reportRef.current = embeddedReport as Report;
    onEmbedded(embeddedReport);

    if (embeddedReport && "getFilters" in embeddedReport) {
      try {
        const reportInstance = embeddedReport as Report;

        // eslint-disable-next-line @typescript-eslint/unbound-method
        const originalSetFilters = reportInstance.setFilters;
        if (originalSetFilters) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore - Deliberately overriding method
          reportInstance.setFilters = function (filters) {
            try {
              // Handle empty filter arrays
              if (!filters || !Array.isArray(filters) || filters.length === 0) {
                return Promise.resolve({
                  body: null,
                  headers: {},
                  statusCode: 200,
                });
              }

              // Filter out any invalid filter objects
              const validFilters = filters.filter(
                (filter) =>
                  filter &&
                  typeof filter === "object" &&
                  Object.keys(filter).length > 0
              );

              // Only pass non-empty array of valid filters
              if (validFilters.length > 0) {
                return originalSetFilters.call(this, validFilters);
              } else {
                return Promise.resolve({
                  body: null,
                  headers: {},
                  statusCode: 200,
                });
              }
            } catch (e) {
              return Promise.reject(e);
            }
          };
        }

        // Fix visual click issues
        const originalVisualClicked = (reportInstance as any).on;
        if (
          originalVisualClicked &&
          typeof originalVisualClicked === "function"
        ) {
          // @ts-ignore - Deliberately overriding method
          reportInstance.on = function (eventName, callback) {
            if (eventName === "visualClicked") {
              // Wrap visual click handler to catch errors
              const wrappedCallback = (event: models.ICustomEvent<any>) => {
                try {
                  // Prevent errors after visual clicks
                  ignoreNextError.current = true;
                  setTimeout(() => {
                    ignoreNextError.current = false;
                  }, 500);

                  callback(event);
                } catch (e) {
                  console.error("Error in visualClicked callback:", e);
                }
              };
              return originalVisualClicked.call(
                this,
                eventName,
                wrappedCallback
              );
            }
            return originalVisualClicked.call(this, eventName, callback);
          };
        }
      } catch (e) {
        console.warn("Could not patch PowerBI behavior:", e);
      }
    }
  };

  const baseEventHandlers = new Map<string, EventHandler>([
    [
      "loaded",
      () => {
        setIsLoaded(true);
      },
    ],
    [
      "rendered",
      () => {
        analyticsService.trackEvent(
          AnalyticsEvents.VIEW_DASHBOARD.FINISH_LOADING,
          {
            analysisId: analysisId,
            sessionId: sessionId,
          }
        );
      },
    ],
    [
      "error",
      (event) => {
        if (event?.detail?.message) {
          const errorMessage = event.detail.message as string;

          // Ignore errors shortly after visual clicks
          if (ignoreNextError.current) {
            return;
          }

          // Handle filter serialization error
          if (
            errorMessage.includes(
              "filter serialization function with no filter"
            ) ||
            errorMessage.includes("visualClickedFailed")
          ) {
            // Just ignore these specific errors
            return;
          }

          // Handle other errors
          console.error("PowerBI Error:", errorMessage);
          analyticsService.trackEvent(
            AnalyticsEvents.VIEW_DASHBOARD.ERROR_LOADING,
            {
              errorMessage,
              errorDetail: event.detail,
              analysisId: analysisId,
              sessionId: sessionId,
            }
          );
          logErrorToSentry(errorMessage, event.detail);
          setError(
            "We encountered an issue while loading the dashboard. This might be due to a temporary connection problem or session timeout."
          );
        }
      },
    ],
  ]);

  const devEventHandlers = new Map<string, EventHandler>([
    [
      "loaded",
      () => {
        console.log("Report loaded");
        setIsLoaded(true);
      },
    ],
    [
      "rendered",
      () => {
        console.log("Report rendered");
      },
    ],
    ["visualClicked", () => console.log("Visual clicked")],
    ["pageChanged", (event) => console.log("Page changed:", event)],
  ]);

  const eventHandlers = new Map([
    ...baseEventHandlers,
    ...(import.meta.env.DEV ? [...devEventHandlers] : []),
  ]);

  return (
    <Box className="h-full w-full relative [&_.reportClass]:absolute [&_.reportClass]:inset-0 [&_.reportClass]:h-full [&_.reportClass]:w-full">
      {!error && (
        <Box
          className="h-full w-full"
          style={{ visibility: isLoaded ? "visible" : "hidden" }}
        >
          <PowerBI
            embedConfig={{ ...embedConfig, settings: DEFAULT_EMBED_SETTINGS }}
            eventHandlers={eventHandlers}
            cssClassName="reportClass"
            getEmbeddedComponent={handleEmbedded}
          />
        </Box>
      )}
      {!isLoaded && (
        <Box className="absolute top-1/2 left-1/2 p-8 text-gray-600 text-center bg-white rounded-xl -translate-x-1/2 -translate-y-1/2">
          <LoadingView />
        </Box>
      )}

      {isLoaded && !error && (
        <Tooltip hasArrow label="Full Screen" openDelay={300} placement="left">
          <IconButton
            className="dashboard-fullscreen-toggle text-gray-800 bg-gray-100/90 shadow-md hover:bg-gray-200 active:bg-gray-300"
            pos="absolute"
            zIndex="100"
            top="4"
            right="4"
            aria-label="Enter full screen"
            colorScheme="gray"
            icon={<MdFullscreen fontSize="1.5rem" />}
            isRound
            onClick={enableFullScreen}
            size="md"
            variant="solid"
          />
        </Tooltip>
      )}

      <ErrorModal
        isOpen={!!error}
        onClose={handleClose}
        onRefresh={handleRefresh}
        error={error}
      />
    </Box>
  );
};
